'use client';

import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { GridState } from 'ag-grid-community';
import type {
  TableState,
  ExternalFilters,
} from '../utils/table-persistence.utils';
import {
  generateChecksum,
  getXDaysAgoInMs,
  TABLE_IDS,
  TABLE_STATE_PREFIX,
  createUserTableSuffix,
} from '../utils/table-persistence.utils';

type ProtectedTablePersistenceState = {
  data: TableState;
  checksum: string;
  version: string;
};

type TablePersistenceStore = {
  states: { [storageKey: string]: ProtectedTablePersistenceState };
  _hasHydrated: boolean;
  setHasHydrated: (state: boolean) => void;

  getTablePersistenceState: (
    storageKey: string,
    tableId: string,
  ) => GridState | null;
  getExternalFilters: (
    storageKey: string,
    tableId: string,
  ) => ExternalFilters | undefined;
  saveTablePersistenceState: (
    storageKey: string,
    tableId: string,
    data: GridState,
    externalFilters?: ExternalFilters,
  ) => void;
  removeTablePersistenceState: (storageKey: string) => void;
  clearFindingsTablePersistenceForUser: (userId: string) => void;
};

const daysToKeepState = 30;

const isInvalidMultiSelectValue = (value: unknown): boolean =>
  value !== undefined && value !== null && !Array.isArray(value);

const hasLegacyFindingsFilterShape = ({
  tableId,
  externalFilters,
}: {
  tableId: string;
  externalFilters?: ExternalFilters;
}): boolean => {
  if (
    tableId !== TABLE_IDS.FINDINGS &&
    tableId !== TABLE_IDS.APPLICATION_FINDINGS
  ) {
    return false;
  }

  if (!externalFilters) {
    return false;
  }

  const { severity, status, application } = externalFilters;

  return (
    isInvalidMultiSelectValue(severity) ||
    isInvalidMultiSelectValue(status) ||
    isInvalidMultiSelectValue(application)
  );
};

const validateAndParseTablePersistenceState = (
  protectedState: ProtectedTablePersistenceState,
  tableId: string,
): TableState | null => {
  try {
    if (
      !protectedState.data ||
      !protectedState.checksum ||
      !protectedState.version
    ) {
      console.warn(
        `Invalid table state format for ${tableId}, resetting to defaults`,
      );

      return null;
    }

    const tableState = protectedState.data as TableState;

    const expectedChecksum = generateChecksum(tableState);

    if (protectedState.checksum !== expectedChecksum) {
      console.warn(
        `Table state checksum mismatch for ${tableId}, data may have been tampered with or corrupted. Resetting to defaults.`,
      );

      return null;
    }

    const daysToKeepStateInMs = getXDaysAgoInMs(daysToKeepState);

    if (tableState.timestamp && tableState.timestamp < daysToKeepStateInMs) {
      console.info(
        `Table state for ${tableId} is older than ${daysToKeepState} days, removing`,
      );

      return null;
    }

    if (
      hasLegacyFindingsFilterShape({
        tableId,
        externalFilters: tableState.externalFilters,
      })
    ) {
      console.info(
        `Table state for ${tableId} has legacy findings filter shape, removing`,
      );

      return null;
    }

    return tableState;
  } catch (parseError) {
    console.warn(`Failed to parse table state for ${tableId}:`, parseError);

    return null;
  }
};

export const tablePersistenceStore = create<TablePersistenceStore>()(
  persist(
    (set, get) => ({
      states: {},
      _hasHydrated: false,
      setHasHydrated: (state: boolean) => {
        set({
          _hasHydrated: state,
        });
      },

      getTablePersistenceState: (
        storageKey: string,
        tableId: string,
      ): GridState | null => {
        const { states } = get();
        const protectedState = states[storageKey];

        if (!protectedState) {
          return null;
        }

        const tableState = validateAndParseTablePersistenceState(
          protectedState,
          tableId,
        );

        if (!tableState) {
          set((currentState: TablePersistenceStore) => {
            const newStates = { ...currentState.states };

            delete newStates[storageKey];

            return {
              ...currentState,
              states: newStates,
            };
          });

          return null;
        }

        return tableState.data;
      },

      getExternalFilters: (
        storageKey: string,
        tableId: string,
      ): ExternalFilters | undefined => {
        const { states } = get();
        const protectedState = states[storageKey];

        if (!protectedState) {
          return undefined;
        }

        const tableState = validateAndParseTablePersistenceState(
          protectedState,
          tableId,
        );

        if (!tableState) {
          set((currentState: TablePersistenceStore) => {
            const newStates = { ...currentState.states };

            delete newStates[storageKey];

            return {
              ...currentState,
              states: newStates,
            };
          });

          return undefined;
        }

        return tableState.externalFilters;
      },

      saveTablePersistenceState: (
        storageKey: string,
        tableId: string,
        data: GridState,
        externalFilters?: ExternalFilters,
      ) => {
        try {
          const { states } = get();
          const existingProtectedState = states[storageKey];
          const existingTableState = existingProtectedState
            ? validateAndParseTablePersistenceState(
                existingProtectedState,
                tableId,
              )
            : null;
          let gridData = data;

          if (!data || Object.keys(data).length === 0) {
            if (existingTableState) {
              gridData = existingTableState.data;
            }
          }

          const stateWithTimestamp: TableState = {
            data: gridData,
            externalFilters:
              externalFilters ?? existingTableState?.externalFilters,
            timestamp: Date.now(),
          };

          const protectedState: ProtectedTablePersistenceState = {
            data: stateWithTimestamp,
            checksum: generateChecksum(stateWithTimestamp),
            version: '1.0',
          };

          set((currentState) => ({
            ...currentState,
            states: {
              ...currentState.states,
              [storageKey]: protectedState,
            },
          }));
        } catch (error) {
          console.warn(`Failed to save table state for ${tableId}:`, error);
        }
      },

      removeTablePersistenceState: (storageKey: string) => {
        set((currentState) => {
          const newStates = { ...currentState.states };

          delete newStates[storageKey];

          return {
            ...currentState,
            states: newStates,
          };
        });
      },

      clearFindingsTablePersistenceForUser: (userId: string) => {
        if (!userId) {
          return;
        }

        const storageKey = `${TABLE_STATE_PREFIX}${createUserTableSuffix(userId)}_${TABLE_IDS.FINDINGS}`;

        get().removeTablePersistenceState(storageKey);
      },
    }),
    {
      name: 'table-persistence',
      // Only persist the states directly, not the methods or hydration flag
      partialize: (state) => ({ states: state.states }),
      onRehydrateStorage: () => (state) => {
        if (state) {
          state.setHasHydrated(true);
          console.debug('Table persistence store hydrated');
        }
      },
    },
  ),
);
